import React, {useState, useEffect, useMemo} from "react";
import {Modal, Typography, Select, Spin} from "antd";
import styled from "styled-components";
import moment from "moment";
import {useStore} from "../../../hooks/useStore";
import {debounce} from "lodash";
import {CourierDetailBox} from "./ShiftEntryModal/CourierDetailBox/CourierDetailBox";

const {Text} = Typography;

const WarningText = styled.div`
    border: 1px solid #ff4d4f;
    padding: 8px 12px;
    margin-bottom: 16px;
    border-radius: 4px;
`;

const CourierInfo = styled.div`
    margin-bottom: 16px;
    display: flex;
    gap: 24px;

    .info-item {
        display: flex;
        gap: 8px;
        align-items: center;
    }
`;

const ShiftInfo = styled.div`
    margin-bottom: 16px;
    padding: 12px;
    background: #f5f5f5;
    border-radius: 4px;

    .info-row {
        display: flex;
        gap: 12px;
        align-items: center;
    }

    .info-label {
        font-weight: 600;
        margin-bottom: 8px;
    }
`;

const SelectContainer = styled.div`
    display: flex;
    gap: 16px;
    margin-bottom: 24px;

    .select-group {
        flex: 1;

        .select-label {
            margin-bottom: 8px;
            font-weight: 500;
            color: rgba(0, 0, 0, 0.85);
        }

        .ant-select {
            width: 100%;
        }
    }
`;

const WarningMessage = styled(Text)`
    color: #ff4d4f;
    display: block;
`;

const ReasonSelect = styled.div`
    margin-bottom: 24px;

    .select-label {
        margin-bottom: 8px;
        font-weight: 500;
        color: rgba(0, 0, 0, 0.85);
    }

    .ant-select {
        width: 100%;
    }
`;

export const timeChangeReasons = [
    {value: "rush_destek", label: "Rush (Destek)"},
    {value: "hatali_vardiya", label: "Hatalı Vardiya"},
    {value: "erken_giris", label: "Erken Giriş"}
];

export const courierChangeReasons = [
    {value: "kaza", label: "Kaza"},
    {value: "motor_arizasi", label: "Motor Arızası"},
    {value: "hatali_vardiya", label: "Hatalı Vardiya"},
    {value: "uyumsuzluk", label: "Uyumsuzluk"}
];

const ModalContent = styled.div`
    .ant-modal-wrap {
        overflow: hidden;
    }

    .ant-modal-body {
        max-height: 80vh;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
    }

    .ant-select-dropdown {
        position: fixed !important;
    }
`;

export const TimeChangeModal = ({visible, onCancel, courier, shiftData, shiftHours, onSuccess}) => {
    const stores = useStore();
    const [startHourId, setStartHourId] = useState(null);
    const [endHourId, setEndHourId] = useState(null);
    const [reason, setReason] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (visible && shiftData) {
            // Mevcut saatleri bul
            const currentStartHour = shiftHours?.find(
                hour => hour.startTime === shiftData.plannedStartTime
            );
            const currentEndHour = shiftHours?.find(
                hour => hour.endTime === shiftData.plannedEndTime
            );

            setStartHourId(currentStartHour?._id || null);
            setEndHourId(currentEndHour?._id || null);
            setReason(null); // Yeni modal açılışında sebebi sıfırla
        }
    }, [visible, shiftData, shiftHours]);

    useEffect(() => {
        if (visible) {
            document.body.classList.add("modal-open");
        } else {
            document.body.classList.remove("modal-open");
        }
        return () => document.body.classList.remove("modal-open");
    }, [visible]);

    const handleSave = async () => {
        if (!startHourId || !endHourId || !reason) {
            stores.appStore.openErrorModal({
                content: "Lütfen tüm alanları doldurunuz."
            });
            return;
        }

        setLoading(true);
        const result = await stores.poolShiftStore.changeCourierPoolShiftHour({
            courierId: courier?._id,
            poolId: shiftData?.poolId,
            date: shiftData?.shiftDate,
            startHourId,
            endHourId,
            reason
        });
        setLoading(false);

        if (result) {
            onSuccess?.();
            onCancel();
        }
    };

    return (
        <ModalContent>
            <Modal
                title="Saat Değiştir"
                visible={visible}
                onCancel={onCancel}
                okText="Kaydet"
                cancelText="İptal"
                width={600}
                confirmLoading={loading}
                onOk={handleSave}
                wrapClassName="pool-shift-modal"
            >
                <CourierInfo>
                    <div className="info-item">
                        <Text strong>Kurye Adı:</Text>
                        <Text>{courier?.name}</Text>
                    </div>
                    <div className="info-item">
                        <Text strong>TCKN:</Text>
                        <Text>{courier?.tckn}</Text>
                    </div>
                </CourierInfo>

                <WarningText>
                    <WarningMessage>
                        1- Toplam çalışma saatleri azaltılamaz
                        <br />
                        2- Vardiya başlangıç saati mevcut başlangıç saatinden ileri alınamaz.
                    </WarningMessage>
                </WarningText>

                <ShiftInfo>
                    <div className="info-label">Vardiya Adı</div>
                    <div className="info-row">
                        <Text>{moment(shiftData?.shiftDate).format("DD MMMM")}</Text>
                        <Text>{shiftData?.shiftName}</Text>
                        <Text>{shiftData?.plannedTime}</Text>
                    </div>
                </ShiftInfo>

                <SelectContainer>
                    <div className="select-group">
                        <div className="select-label">Başlangıç Saati</div>
                        <Select
                            data-testid="shiftStartHourId"
                            value={startHourId}
                            placeholder="Başlangıç saati seçin"
                            onChange={value => setStartHourId(value)}
                            getPopupContainer={trigger => trigger.parentNode}
                        >
                            {shiftHours?.map(shiftHour => (
                                <Select.Option key={shiftHour?._id} value={shiftHour?._id}>
                                    {shiftHour?.startTime}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>

                    <div className="select-group">
                        <div className="select-label">Bitiş Saati</div>
                        <Select
                            data-testid="shiftEndHourId"
                            value={endHourId}
                            placeholder="Bitiş saati seçin"
                            onChange={value => setEndHourId(value)}
                            getPopupContainer={trigger => trigger.parentNode}
                        >
                            {shiftHours?.map(shiftHour => (
                                <Select.Option key={shiftHour?._id} value={shiftHour?._id}>
                                    {shiftHour?.endTime}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                </SelectContainer>

                <ReasonSelect>
                    <div className="select-label">Değişim Sebebi</div>
                    <Select
                        placeholder="Değişim sebebi seçin"
                        onChange={value => setReason(value)}
                        value={reason}
                        getPopupContainer={trigger => trigger.parentNode}
                    >
                        {timeChangeReasons.map(reason => (
                            <Select.Option key={reason.value} value={reason.value}>
                                {reason.label}
                            </Select.Option>
                        ))}
                    </Select>
                </ReasonSelect>
            </Modal>
        </ModalContent>
    );
};

export const CourierChangeModal = ({
    visible,
    onCancel,
    courier,
    shiftData,
    shiftHours,
    onSuccess
}) => {
    const stores = useStore();
    const [searchText, setSearchText] = useState("");
    const [selectedCourierId, setSelectedCourierId] = useState(courier?._id || null);
    const [selectedCourierLabel, setSelectedCourierLabel] = useState(courier?.name || "");
    const [startHourId, setStartHourId] = useState(null);
    const [endHourId, setEndHourId] = useState(null);
    const [reason, setReason] = useState(null);
    const [loading, setLoading] = useState(false);
    const [courierLoading, setCourierLoading] = useState(false);
    const [courierOptions, setCourierOptions] = useState([]);
    // Arama sonucu değiştikçe seçili kurye listeden düşebildiği için ayrıca tutulur.
    const [selectedCourier, setSelectedCourier] = useState(courier || null);

    const onSearchCourier = useMemo(() => debounce(text => setSearchText(text), 800), []);

    // Havuza vardiya yazılabilecek (kullanıcının yetki kapsamındaki) aktif kuryeler; arama
    // sunucuda yapılır ve ilk 10 kurye gelir. Geç dönen eski aramanın yanıtı yok sayılır.
    useEffect(() => {
        if (!visible) return;
        let isCancelled = false;
        (async () => {
            setCourierLoading(true);
            const res = await stores.poolShiftStore.getPoolCouriers({
                poolId: shiftData?.poolId,
                searchText
            });
            if (isCancelled) return;
            setCourierOptions(res?.data || []);
            setCourierLoading(false);
        })();
        return () => {
            isCancelled = true;
        };
    }, [visible, shiftData?.poolId, searchText]);

    useEffect(() => {
        if (visible) {
            setSelectedCourierId(courier?._id || null);
            setSelectedCourierLabel(courier?.name || "");
            setSelectedCourier(courier || null);
            setReason(null);
            setSearchText("");
            // Saatleri de setle
            const currentStartHour = shiftHours?.find(
                hour => hour.startTime === shiftData?.plannedStartTime
            );
            const currentEndHour = shiftHours?.find(
                hour => hour.endTime === shiftData?.plannedEndTime
            );
            setStartHourId(currentStartHour?._id || null);
            setEndHourId(currentEndHour?._id || null);
        }
    }, [visible, courier, shiftData, shiftHours]);

    useEffect(() => {
        if (visible) {
            document.body.classList.add("modal-open");
        } else {
            document.body.classList.remove("modal-open");
        }
        return () => document.body.classList.remove("modal-open");
    }, [visible]);

    const handleCourierChange = value => {
        setSelectedCourierId(value);
        setSelectedCourier(courierOptions.find(c => c._id === value) || null);
    };

    const handleSave = async () => {
        if (!selectedCourierId || !startHourId || !endHourId || !reason) {
            stores.appStore.openErrorModal({
                content: "Lütfen tüm alanları doldurunuz."
            });
            return;
        }
        setLoading(true);
        const result = await stores.poolShiftStore.changeCourierPoolShift({
            courierId: courier?._id,
            newCourierId: selectedCourierId,
            poolId: shiftData?.poolId,
            date: shiftData?.shiftDate,
            startHourId,
            endHourId,
            reason
        });
        setLoading(false);

        if (result) {
            onSuccess?.();
            onCancel();
        }
    };

    return (
        <ModalContent>
            <Modal
                title="Kurye Değiştir"
                visible={visible}
                onCancel={onCancel}
                okText="Kaydet"
                cancelText="İptal"
                width={600}
                confirmLoading={loading}
                onOk={handleSave}
                wrapClassName="pool-shift-modal"
            >
                <CourierInfo>
                    <div className="info-item">
                        <Text strong>Kurye Adı:</Text>
                        <Text>{courier?.name}</Text>
                    </div>
                    <div className="info-item">
                        <Text strong>TCKN:</Text>
                        <Text>{courier?.tckn}</Text>
                    </div>
                </CourierInfo>

                <WarningText>
                    <WarningMessage>
                        1- Toplam çalışma saatleri azaltılamaz
                        <br />
                        2- Vardiya başlangıç saati mevcut başlangıç saatinden ileri alınamaz.
                    </WarningMessage>
                </WarningText>

                <ShiftInfo>
                    <div className="info-label">Vardiya Adı</div>
                    <div className="info-row">
                        <Text>{moment(shiftData?.shiftDate).format("DD MMMM")}</Text>
                        <Text>{shiftData?.shiftName}</Text>
                        <Text>{shiftData?.plannedTime}</Text>
                    </div>
                </ShiftInfo>

                <SelectContainer>
                    <div className="select-group">
                        <div className="select-label">Başlangıç Saati</div>
                        <Select
                            data-testid="shiftStartHourId"
                            value={startHourId}
                            placeholder="Başlangıç saati seçin"
                            onChange={value => setStartHourId(value)}
                            getPopupContainer={trigger => trigger.parentNode}
                        >
                            {shiftHours?.map(shiftHour => (
                                <Select.Option key={shiftHour?._id} value={shiftHour?._id}>
                                    {shiftHour?.startTime}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>

                    <div className="select-group">
                        <div className="select-label">Bitiş Saati</div>
                        <Select
                            data-testid="shiftEndHourId"
                            value={endHourId}
                            placeholder="Bitiş saati seçin"
                            onChange={value => setEndHourId(value)}
                            getPopupContainer={trigger => trigger.parentNode}
                        >
                            {shiftHours?.map(shiftHour => (
                                <Select.Option key={shiftHour?._id} value={shiftHour?._id}>
                                    {shiftHour?.endTime}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                </SelectContainer>

                <div style={{marginBottom: 24}}>
                    <div
                        className="select-label"
                        style={{marginBottom: 8, fontWeight: 500, color: "rgba(0,0,0,0.85)"}}
                    >
                        Kurye Seç
                    </div>
                    <Select
                        showSearch
                        allowClear
                        value={selectedCourierId || undefined}
                        placeholder="Kurye ara ve seç"
                        onChange={handleCourierChange}
                        onSearch={onSearchCourier}
                        notFoundContent={courierLoading ? <Spin size="small" /> : null}
                        style={{width: "100%"}}
                        optionLabelProp="label"
                        getPopupContainer={trigger => trigger.parentNode}
                        filterOption={false}
                    >
                        {/* Eğer seçili kurye courierOptions'ta yoksa, onu da ekle */}
                        {selectedCourierId &&
                            selectedCourier?._id === selectedCourierId &&
                            !courierOptions.some(c => c._id === selectedCourierId) && (
                                <Select.Option
                                    key={selectedCourier._id}
                                    value={selectedCourier._id}
                                    label={selectedCourier.name || selectedCourier.fullname}
                                >
                                    <CourierDetailBox
                                        courierName={
                                            selectedCourier.name || selectedCourier.fullname
                                        }
                                        courierTckn={selectedCourier.tckn}
                                        courierCellPhone={selectedCourier.cellPhone}
                                    />
                                </Select.Option>
                            )}
                        {courierOptions.map(c => (
                            <Select.Option key={c._id} value={c._id} label={c.name || c.fullname}>
                                <CourierDetailBox
                                    courierName={c.name || c.fullname}
                                    courierTckn={c.tckn}
                                    courierCellPhone={c.cellPhone}
                                />
                            </Select.Option>
                        ))}
                    </Select>
                </div>

                <ReasonSelect>
                    <div className="select-label">Değişim Sebebi</div>
                    <Select
                        placeholder="Değişim sebebi seçin"
                        onChange={value => setReason(value)}
                        value={reason}
                        getPopupContainer={trigger => trigger.parentNode}
                    >
                        {courierChangeReasons.map(reason => (
                            <Select.Option key={reason.value} value={reason.value}>
                                {reason.label}
                            </Select.Option>
                        ))}
                    </Select>
                </ReasonSelect>
            </Modal>
        </ModalContent>
    );
};
